import { FC, MouseEvent, useMemo } from 'react'
import classNames from 'classnames'

import {
    compareAsc,
    isBefore,
    isSameDay,
    startOfDay,
} from '../../../helpers/date'
import { SpecialDayViewModel } from '../../../models'
import { CalendarItem, CalenderDayInterface } from '../../entities/Shift/Shift'
import { doesCalendarItemFallOnWorkDay } from './helpers'
import { CalendarDayContent, CalendarDayHeader } from './subcomponents'

import './CalendarDay.scss'

interface CalenderDayProps extends CalenderDayInterface {
    className?: string;
}

const CalenderDay: FC<CalenderDayProps> = ({
    isDark,
    isOutOfRange,
    showAddItemButton,
    showAddSequenceButton,
    showDayInMonth,
    showHeader,
    addItemButtonTooltipText,
    addSequenceButtonTooltipText,
    day,
    dayHeaderFormat,
    items,
    specialDays,
    onAddItemButtonClick,
    onAddSequenceButtonClick,
    onDayDragEnter,
    onDayDrop,
    className = '',
}) => {
    const isPast = useMemo((): boolean => isBefore(day, startOfDay(new Date())), [day])
    const filteredSpecialDays = useMemo((): SpecialDayViewModel[] => specialDays.filter((specialDay) => isSameDay(specialDay.date, day)), [specialDays])
    const filteredItems = useMemo(
        (): CalendarItem[] => items
            .filter((item) => doesCalendarItemFallOnWorkDay(item, day))
            .sort((a, b) => compareAsc(a.start, b.start)),
        [items, day],
    )

    const calendarDayClassNames = classNames('calendar-day', {
        'calendar-day--is-out-of-range': isOutOfRange,
    }, className)

    const handleElementDrop = () : void => {
        if (onDayDrop) {
            onDayDrop(isPast ? undefined : day)
        }
    }

    const handleElementDragEnter = (): void => {
        if (onDayDragEnter) {
            onDayDragEnter(isPast ? undefined : day)
        }
    }

    const handleElementDragOver = (e: MouseEvent): void => {
        e.preventDefault()
    }

    return (
        <div
            onDragOver={handleElementDragOver}
            onDragEnter={handleElementDragEnter}
            onDrop={handleElementDrop}
            className={calendarDayClassNames}
        >
            {showHeader && (
                <CalendarDayHeader
                    day={day}
                    dayHeaderFormat={dayHeaderFormat}
                    className="calendar-day__header"
                />
            )}

            <CalendarDayContent
                isDark={isDark}
                showAddItemButton={showAddItemButton}
                showDayInMonth={showDayInMonth}
                showAddSequenceButton={showAddSequenceButton}
                addItemButtonTooltipText={addItemButtonTooltipText}
                addSequenceButtonTooltipText={addSequenceButtonTooltipText}
                day={day}
                specialDays={filteredSpecialDays}
                onAddItemButtonClick={onAddItemButtonClick}
                onAddSequenceButtonClick={onAddSequenceButtonClick}
                className="calendar-day__content"
            >
                {filteredItems.map(item => item.element)}
            </CalendarDayContent>
        </div>
    )
}

export default CalenderDay
